import { useState } from 'react';
import type { ExportJob, ExportPhase, ExportStatus } from '@atlas/shared';
import { ExportDetailModal } from './ExportDetailModal.js';

interface ExportsTableProps {
  exports: Array<ExportJob & { id: string }>;
  loading?: boolean;
  emptyMessage?: string;
}

const PHASE_LABELS: Record<ExportPhase, string> = {
  discovering: 'Discovering tables',
  exporting_tables: 'Exporting tables',
  validating: 'Validating',
  transferring_attachments: 'Transferring files',
  provisioning_access: 'Provisioning access',
};

const STATUS_STYLES: Record<ExportStatus, string> = {
  queued: 'bg-[#f1f1ef] text-[#73726e]',
  running: 'bg-[#e7f0fb] text-[#2383e2]',
  complete: 'bg-[#e5f5f2] text-[#0f7b6c]',
  failed: 'bg-[#fbe8e8] text-[#e03e3e]',
  cancelled: 'bg-[#f1f1ef] text-[#aeaaa4]',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusCell({ exp }: { exp: ExportJob }) {
  const label =
    exp.status === 'running' && exp.phase ? PHASE_LABELS[exp.phase] : exp.status;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[exp.status]}`}
    >
      {exp.status === 'running' && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {label}
    </span>
  );
}

export function ExportsTable({
  exports,
  loading = false,
  emptyMessage = 'No exports yet.',
}: ExportsTableProps) {
  const [detail, setDetail] = useState<(ExportJob & { id: string }) | null>(null);

  if (loading) {
    return <div className="py-12 text-center text-sm text-[#aeaaa4]">Loading…</div>;
  }
  if (exports.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-[#aeaaa4]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {detail && <ExportDetailModal exp={detail} onClose={() => setDetail(null)} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#e9e9e7]">
              {['Company', 'Agency', 'Created by', 'Started', 'Status', 'Tables', 'SFTP host'].map(
                (h, i) => (
                  <th
                    key={i}
                    className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {exports.map((exp) => (
              <tr
                key={exp.id}
                onClick={() => setDetail(exp)}
                className="border-b border-[#f1f1ef] hover:bg-[#fafafa] transition-colors cursor-pointer"
              >
                <td className="py-2.5 px-3 font-medium text-[#37352f] whitespace-nowrap">
                  {exp.companyName}
                </td>
                <td className="py-2.5 px-3 text-[#aeaaa4] font-mono text-xs whitespace-nowrap max-w-[160px] truncate">
                  {exp.agencyId}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                  {exp.createdBy.displayName}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap text-xs">
                  {formatDate(exp.createdAt)}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap">
                  <StatusCell exp={exp} />
                </td>
                <td className="py-2.5 px-3 text-[#73726e] tabular-nums whitespace-nowrap">
                  {exp.validation?.tablesExported ??
                    (exp.tableResults.length || '—')}
                </td>
                <td className="py-2.5 px-3 text-[#aeaaa4] text-xs font-mono whitespace-nowrap max-w-[200px] truncate">
                  {exp.sftp ? exp.sftp.endpoint : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
